import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { Platform } from 'react-native';

export interface VideoBackgroundProps {
    /** URL of the video to display */
    videoUrl?: string | null;
    /** Fallback image URL when video is not available */
    imageUrl?: string | null;
    /** Overlay opacity (0-1), defaults to 0.4 */
    overlayOpacity?: number;
    /** Overlay color, defaults to black */
    overlayColor?: string;
    /** Whether to show gradient overlay instead of solid */
    gradientOverlay?: boolean;
    /** Custom gradient direction */
    gradientDirection?: string;
    /** Children to render on top of the background */
    children?: React.ReactNode;
    /** Additional styles for the container */
    sx?: object;
    /** Min height of the container */
    minHeight?: string | number;
}

/**
 * VideoBackground - A reusable component for video/image hero backgrounds
 * 
 * Features:
 * - Supports video with autoplay, muted, loop
 * - Falls back to image when video not provided
 * - Configurable dark overlay
 * - Works on web platform
 */
export const VideoBackground: React.FC<VideoBackgroundProps> = ({
    videoUrl,
    imageUrl,
    overlayOpacity = 0.4,
    overlayColor = '#000000',
    gradientOverlay = false,
    gradientDirection = 'to bottom',
    children,
    sx = {},
    minHeight = '100vh',
}) => {
    // Only render video on web platform
    const isWeb = Platform.OS === 'web';
    const hasVideo = isWeb && !!videoUrl;
    const hasImage = !!imageUrl;

    const videoRef = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = true;
        }
    }, []);

    // Generate overlay style
    const overlayStyle = gradientOverlay
        ? `linear-gradient(${gradientDirection}, ${overlayColor}${Math.round(overlayOpacity * 255).toString(16).padStart(2, '0')}, transparent, ${overlayColor}${Math.round(overlayOpacity * 255).toString(16).padStart(2, '0')})`
        : `${overlayColor}${Math.round(overlayOpacity * 255).toString(16).padStart(2, '0')}`;

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                minHeight,
                overflow: 'hidden',
                ...sx,
            }}
        >
            {/* Video Background */}
            {hasVideo && (
                <Box
                    component='video'
                    ref={videoRef}
                    autoPlay
                    muted
                    loop
                    playsInline
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        minWidth: '100%',
                        minHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        transform: 'translate(-50%, -50%)',
                        objectFit: 'cover',
                        zIndex: 0,
                    }}
                >
                    <source src={videoUrl} type='video/mp4' />
                </Box>
            )}

            {/* Image Background (fallback or primary if no video) */}
            {!hasVideo && hasImage && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: `url(${imageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        zIndex: 0,
                    }}
                />
            )}

            {/* Overlay */}
            {(hasVideo || hasImage) && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: overlayStyle,
                        zIndex: 1,
                    }}
                />
            )}

            {/* Content */}
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    width: '100%',
                    height: '100%',
                    minHeight,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default VideoBackground;
